import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';

export default function Footer() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  /** Navigate to home page and scroll to section */
  const navigateToSection = (sectionId: string) => {
    if (location.pathname === '/') {
      // If already on home page, just scroll
      const element = document.getElementById(sectionId);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    } else {
      // If on other page, navigate to home first then scroll
      navigate('/');
      setTimeout(() => {
        const element = document.getElementById(sectionId);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    }
  };

  /** Navigate to pricing calculator section */
  const navigateToPriceCalculator = () => {
    if (location.pathname === '/') {
      // If already on home page, scroll to price calculator
      const element = document.querySelector('.bg-gray-900.rounded-2xl.p-8.border.border-gray-800');
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    } else {
      // If on other page, navigate to home first then scroll to calculator
      navigate('/');
      setTimeout(() => {
        const element = document.querySelector('.bg-gray-900.rounded-2xl.p-8.border.border-gray-800');
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 100);
    }
  };
  
  return (
    <footer className="bg-gray-950 py-16 px-6">
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-12">
          <div className="md:col-span-2">
            <div className="inline-block mb-6 group cursor-pointer">
              <img 
                src="https://static.readdy.ai/image/14090482ad00f674843640af3b243289/9c8773d1b1b036c04dea96b1d431535f.png" 
                alt={t('flidgeLogo')}
                className="h-8 w-auto transition-all duration-300 group-hover:scale-110"
              />
            </div>
            <p className="text-gray-400 mb-6 max-w-md">
              {t('footerDescription')}
            </p>
            <div className="flex space-x-4">
              <a 
                href="https://x.com/FlidgeAI" 
                className="w-10 h-10 rounded-full bg-gray-800 flex items-center justify-center text-gray-400 hover:bg-indigo-500 hover:text-white transition-colors cursor-pointer"
              >
                <i className="ri-twitter-x-line ri-lg"></i>
              </a>
              <a 
                href="https://www.instagram.com/flidgeai/" 
                className="w-10 h-10 rounded-full bg-gray-800 flex items-center justify-center text-gray-400 hover:bg-indigo-500 hover:text-white transition-colors cursor-pointer"
              >
                <i className="ri-instagram-line ri-lg"></i>
              </a>
              <a 
                href="https://www.facebook.com/flidgeai" 
                className="w-10 h-10 rounded-full bg-gray-800 flex items-center justify-center text-gray-400 hover:bg-indigo-500 hover:text-white transition-colors cursor-pointer"
              >
                <i className="ri-facebook-line ri-lg"></i>
              </a>
            </div>
          </div>
          
          <div>
            <h5 className="font-bold mb-6 text-white">{t('services')}</h5>
            <ul className="space-y-3">
              <li>
                <button 
                  onClick={() => navigateToSection('features')}
                  className="text-gray-400 hover:text-indigo-400 transition-colors cursor-pointer"
                >
                  {t('featuresLink')}
                </button>
              </li>
              <li>
                <button 
                  onClick={navigateToPriceCalculator}
                  className="text-gray-400 hover:text-indigo-400 transition-colors cursor-pointer"
                >
                  {t('pricingConsult')}
                </button>
              </li>
            </ul>
          </div>
          
          <div>
            <h3 className="text-lg font-semibold mb-4">{t('companySection')}</h3>
            <ul className="space-y-2">
              <li>
                <a href="#about" className="text-gray-400 hover:text-indigo-400 transition-colors cursor-pointer">
                  {t('aboutUs')}
                </a>
              </li>
            </ul>
          </div>
        </div>
        
        <div className="pt-8 border-t border-gray-800 flex flex-col md:flex-row justify-between items-center">
          <p className="text-gray-500 mb-4 md:mb-0">
            {t('copyright')}
          </p>
          <div className="flex flex-wrap gap-4">
            <a href="/terms" className="text-gray-500 hover:text-indigo-400 transition-colors cursor-pointer">
              {t('termsOfService')}
            </a>
            <a href="/privacy" className="text-gray-500 hover:text-indigo-400 transition-colors cursor-pointer">
              {t('privacyPolicy')}
            </a>
            <a href="/cookies" className="text-gray-500 hover:text-indigo-400 transition-colors cursor-pointer">
              {t('cookiePolicy')}
            </a>
            <a 
              href="https://readdy.ai/?origin=logo" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-gray-500 hover:text-indigo-400 transition-colors cursor-pointer"
            >
              {t('poweredBy')}
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
}
